import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

/**
 * One Home tile: icon + title (the title is the link to the full page), an optional count and
 * right-hand control (tabs), then the rows. Every tile fills its grid cell, so the four line up.
 */
export default function PilotTile({
  icon: Icon,
  title,
  href,
  onTitleClick,
  count,
  right,
  highlight = false,
  noChevron = false,
  children,
  testId,
}) {
  const heading = (
    <span className="flex items-center gap-1.5 group">
      {Icon ? <Icon className="w-4 h-4 text-[#00C2C2] flex-shrink-0" aria-hidden="true" /> : null}
      <span className="text-sm font-semibold text-[#0A1A2F] group-hover:text-[#00C2C2] transition-colors">{title}</span>
      {count != null ? (
        <Badge variant="secondary" className={`text-xs h-5 px-1.5 ${highlight ? 'bg-white/70 border border-gray-200' : ''}`}>
          {count}
        </Badge>
      ) : null}
      {noChevron ? null : <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#00C2C2] transition-colors" aria-hidden="true" />}
    </span>
  );
  return (
    <Card
      data-testid={testId}
      className={`border-0 shadow-md h-full flex flex-col ${highlight ? 'bg-blue-100' : 'bg-white'}`}
    >
      <CardContent className="p-4 md:p-6 flex flex-col gap-3 flex-1 min-h-0">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {href ? (
            <Link to={href} className="min-w-0">{heading}</Link>
          ) : (
            <button type="button" onClick={onTitleClick} className="min-w-0 text-left">{heading}</button>
          )}
          {right}
        </div>
        <div className="flex flex-col gap-1 flex-1 min-h-0">{children}</div>
      </CardContent>
    </Card>
  );
}

export function TileTabs({ tabs, value, onChange, label }) {
  return (
    <div role="tablist" aria-label={label} className="inline-flex gap-0.5 rounded-md bg-[#ECFFFE] p-0.5">
      {tabs.map((tab) => {
        const on = tab.value === value;
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(tab.value)}
            className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
              on ? 'bg-white text-[#0A1A2F] shadow-sm' : 'text-[#5C6B7A] hover:text-[#0A1A2F]'
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export function TileEmpty({ children }) {
  return <p className="py-6 text-center text-xs text-[#5C6B7A]">{children}</p>;
}

export function TileSkeleton({ rows = 5 }) {
  return (
    <div className="flex flex-col gap-2" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-12 rounded-md bg-gray-100 animate-pulse" />
      ))}
    </div>
  );
}
